const KEY = 'kabaak_v1';

const DEFAULT = {
  username: '',
  totalScore: 0,
  gamesPlayed: 0,
  totalCorrect: 0,
  totalQuestions: 0,
  bestStreak: 0,
  duelWins: 0,
  earnedBadges: [],
  civStats: {},   // { [civKey]: { rounds, correct, total, bestPct } }
  history: [],    // [{ score, correct, total, civ, diff, date }]
};

export function getPlayer() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...DEFAULT, ...JSON.parse(raw) } : { ...DEFAULT };
  } catch {
    return { ...DEFAULT };
  }
}

function save(p) {
  localStorage.setItem(KEY, JSON.stringify(p));
  return p;
}

export function updateUsername(player, name) {
  return save({ ...player, username: name });
}

export function recordGame(player, { score, correct, total, civ, diff, bestStreak }) {
  const p = { ...player };
  p.gamesPlayed    = (p.gamesPlayed    || 0) + 1;
  p.totalScore     = (p.totalScore     || 0) + score;
  p.totalCorrect   = (p.totalCorrect   || 0) + correct;
  p.totalQuestions = (p.totalQuestions || 0) + total;
  p.bestStreak     = Math.max(p.bestStreak || 0, bestStreak);

  const cs = { ...(p.civStats?.[civ] || { rounds: 0, correct: 0, total: 0, bestPct: 0 }) };
  cs.rounds  += 1;
  cs.correct += correct;
  cs.total   += total;
  cs.bestPct  = Math.max(cs.bestPct, total > 0 ? correct / total : 0);
  p.civStats = { ...p.civStats, [civ]: cs };

  p.history = [
    { score, correct, total, civ, diff, date: Date.now() },
    ...(p.history || []),
  ].slice(0, 200);

  p.earnedBadges = computeEarnedBadges(p);

  return save(p);
}

export function recordDuelWin(player) {
  const p = { ...player, duelWins: (player.duelWins || 0) + 1 };
  p.earnedBadges = computeEarnedBadges(p);
  return save(p);
}

function computeEarnedBadges(p) {
  const earned = new Set(p.earnedBadges || []);

  // 0 — Seeker of Ma'at: complete first quiz
  if (p.gamesPlayed >= 1) earned.add(0);

  // 1 — Student of Sankore: 10 correct answers total
  if (p.totalCorrect >= 10) earned.add(1);

  // 2 — Companion of Averroes: 80%+ on Moors at Medium
  if ((p.history || []).some(h => h.civ === 'moors' && h.diff === 'scholar' && h.total > 0 && h.correct / h.total >= 0.8))
    earned.add(2);

  // 3 — Archer of Meroe: 80%+ on Nubia at any diff
  if ((p.civStats?.nubia?.bestPct || 0) >= 0.8) earned.add(3);

  // 4 — Lion of Aksum: win 3 duels
  if ((p.duelWins || 0) >= 3) earned.add(4);

  // 5 — Rider of Hannibal: complete Carthage at any diff
  if ((p.civStats?.carthage?.rounds || 0) > 0) earned.add(5);

  // 6 — Mansa: accumulate 2000+ total score
  if (p.totalScore >= 2000) earned.add(6);

  // 7 — Grandmaster: complete 8+ different civs on Hard
  const hardCivs = new Set((p.history || []).filter(h => h.diff === 'sage').map(h => h.civ));
  if (hardCivs.size >= 8) earned.add(7);

  return [...earned];
}

// Convert remote (snake_case) profile to local (camelCase) format
export function fromRemoteProfile(remote) {
  return {
    ...DEFAULT,
    username:      remote.username      || '',
    totalScore:    remote.total_score   || 0,
    gamesPlayed:   remote.games_played  || 0,
    totalCorrect:  remote.total_correct || 0,
    bestStreak:    remote.best_streak   || 0,
    duelWins:      remote.duel_wins     || 0,
    earnedBadges:  remote.earned_badges || [],
    civStats:      remote.civ_stats     || {},
  };
}

// Merge local + remote, taking the best of both
export function mergeLocalWithRemote(local, remote) {
  const merged = {
    ...DEFAULT,
    username:      remote.username      || local.username      || '',
    totalScore:    Math.max(local.totalScore   || 0, remote.total_score   || 0),
    gamesPlayed:   Math.max(local.gamesPlayed  || 0, remote.games_played  || 0),
    totalCorrect:  Math.max(local.totalCorrect || 0, remote.total_correct || 0),
    totalQuestions: local.totalQuestions || 0,
    bestStreak:    Math.max(local.bestStreak   || 0, remote.best_streak   || 0),
    duelWins:      Math.max(local.duelWins     || 0, remote.duel_wins     || 0),
    earnedBadges:  [...new Set([...(local.earnedBadges || []), ...(remote.earned_badges || [])])],
    civStats:      mergeCivStats(local.civStats || {}, remote.civ_stats || {}),
    history:       local.history || [],
  };
  return save(merged);
}

function mergeCivStats(local, remote) {
  const result = { ...local };
  for (const [civ, rs] of Object.entries(remote)) {
    const ls = result[civ] || { rounds: 0, correct: 0, total: 0, bestPct: 0 };
    result[civ] = {
      rounds:  Math.max(ls.rounds,  rs.rounds  || 0),
      correct: Math.max(ls.correct, rs.correct || 0),
      total:   Math.max(ls.total,   rs.total   || 0),
      bestPct: Math.max(ls.bestPct, rs.bestPct || 0),
    };
  }
  return result;
}

// Compute display level from total score
const LEVELS = [
  { min: 5000, title: "Grandmaster of Khufu 𓆙",    next: null,        nextMin: null  },
  { min: 3000, title: "Mansa — Lord of Kings 👑",    next: "Grandmaster of Khufu 𓆙",  nextMin: 5000 },
  { min: 2000, title: "Wali of Al-Andalus 🕌",       next: "Mansa 👑",  nextMin: 3000 },
  { min: 1000, title: "Scholar of Timbuktu 📜",      next: "Wali 🕌",   nextMin: 2000 },
  { min: 500,  title: "Companion of Averroes 🏛️",    next: "Scholar 📜",nextMin: 1000 },
  { min: 250,  title: "Archer of Meroe 🏹",          next: "Companion 🏛️",nextMin: 500 },
  { min: 100,  title: "Student of Sankore 📖",       next: "Archer 🏹", nextMin: 250  },
  { min: 0,    title: "Seeker of Ma'at 𓂀",          next: "Student 📖",nextMin: 100  },
];

export function getLevel(totalScore) {
  const score = totalScore || 0;
  const lvl   = LEVELS.find(l => score >= l.min) || LEVELS[LEVELS.length - 1];
  const idx   = LEVELS.indexOf(lvl);
  const prevMin = idx < LEVELS.length - 1 ? LEVELS[idx + 1].min : 0;
  const range   = (lvl.nextMin || lvl.min + 500) - prevMin;
  const pct     = lvl.next ? Math.round(((score - prevMin) / range) * 100) : 100;
  const xpLeft  = lvl.next ? (lvl.nextMin - score) : 0;
  return { title: lvl.title, next: lvl.next, pct: Math.min(pct, 100), xpLeft };
}
